import { Card, Group, LoadingOverlay, Text } from '@mantine/core'
import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { arrayOfObjectsSearch } from '@semios/app-platform-common'
import { ConfirmationSettingsModal } from 'components/ConfirmationSettingsModal/ConfirmationSettingsModal'
import { HierarchicalSelection } from 'components/HierarchicalSelection/HierarchicalSelection'
import { ModalDrawer } from 'components/ModalDrawer/ModalDrawer'
import { translate } from 'i18n/i18n'
import { isEqual } from 'lodash'
import { memo, useEffect, useMemo, useState } from 'react'
import { colors } from 'settings/colors'
import { SharedSettings } from 'settings/SharedSettings'
import { apiFetch } from 'utils/apiFetch'
import { checkAuthorization } from 'utils/checkAuthorization'
import { ApiResponseError, OfflineRequestError } from 'utils/errorCodes'
import { showNotification } from 'utils/showNotification'
import { sortByKey } from 'utils/sortByKey'
import type { TAdminPermission, TAdminProperty } from '../../../utils/useAdminEntites'
import { CancelSaveButtonGroup } from '../../CancelSaveButtonGroup/CancelSaveButtonGroup'
import { PermissionRow } from '../../PermissionRow/PermissionRow'

type TPropertyWithPermissions = NonNullable<routes.AdminGetPropertyWithPermissions.Response['data']>[number]

const PermissionsModal = ({
  opened,
  onClose,
  property,
  permissions,
}: {
  property: TAdminProperty
  permissions: TAdminPermission[]
  opened: boolean
  onClose: (hasChanges?: boolean) => void
}) => {
  const { name, id: propertyId } = property || { name: '', id: 0 }
  const [loading, setIsLoading] = useState<boolean>(false)
  const [searchString, setSearchString] = useState<string | undefined>()
  const [showOnlyChecked, setShowOnlyChecked] = useState(false)
  const [showConfirmCancelWithoutSaving, setShowConfirmCancelWithoutSaving] = useState(false)

  const [permissionsToCreate, setPermissionsToCreate] = useState(
    new Set<routes.AdminUpdatePropertyPermissions.Request['permissionsToCreate'][number]>(),
  )

  const [permissionsToDelete, setPermissionsToDelete] = useState(
    new Set<routes.AdminUpdatePropertyPermissions.Request['permissionsToDelete'][number]>(),
  )

  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([])

  const [effectivePermissions, setEffectivePermissions] = useState<
    TPropertyWithPermissions['effectivePermissions']
  >([])

  const hasSetPropertyPermissionsPermission = checkAuthorization({
    permission: 'ADMIN_SET_PROPERTY_PERMISSIONS',
    entity: '*',
  })

  const handleResetAndClose = (hasChanges?: boolean) => {
    setSearchString(undefined)

    setShowOnlyChecked(false)

    setPermissionsToCreate(new Set())

    setPermissionsToDelete(new Set())

    onClose(hasChanges)
  }

  const hasChanges = permissionsToCreate.size > 0 || permissionsToDelete.size > 0

  const handleClickClose = () => {
    if (hasChanges) {
      setShowConfirmCancelWithoutSaving(true)
    } else {
      handleResetAndClose()
    }
  }

  useEffect(() => {
    const loadPropertyData = async () => {
      try {
        setIsLoading(true)

        const result = await apiFetch({
          url: '/admin-get-property-with-permissions',
          body: { propertyId },
          params: { timeout: 180000 },
        })

        if (result?.data) {
          const effectivePermissions = result.data[0]?.effectivePermissions || []

          setEffectivePermissions(effectivePermissions)

          const appliedPermissions = result.data[0]?.permissions || []
          const selectedPermissions = appliedPermissions.map(({ permission_id }) => permission_id)

          setSelectedPermissions(selectedPermissions)
        } else {
          showNotification({
            type: 'error',
            message: translate.phrases.banyanApp('Error: could not load property data'),
          })
        }
      } catch (error) {
        if (error instanceof ApiResponseError && error.statusCode === 403) {
          showNotification({
            type: 'error',
            message: translate.phrases.banyanApp('You do not have permission to view property data.'),
          })
        } else if (!(error instanceof OfflineRequestError)) {
          showNotification({
            type: 'error',
            message: translate.phrases.banyanApp('Error: could not load property data'),
          })
        }
      } finally {
        setIsLoading(false)
      }
    }

    loadPropertyData()
  }, [property])

  const propertyPermissions = useMemo(() => {
    const propertyPermissions = (permissions ?? []).filter((p) => String(p.permissionTypes) === 'PROPERTY')

    let filteredPermissions = arrayOfObjectsSearch<TAdminPermission>(
      propertyPermissions,
      searchString ?? '',
      ['id'],
    )

    if (showOnlyChecked) {
      filteredPermissions = filteredPermissions.filter((p) => selectedPermissions.includes(p.id))
    }

    return filteredPermissions.sort(sortByKey('id'))
  }, [searchString, showOnlyChecked, selectedPermissions, permissions])

  const modalTitle = translate.phrases.banyanApp('Set Permissions for {{name}} ({{propertyId}})', {
    name,
    propertyId,
  })

  const handleSave = async () => {
    setIsLoading(true)

    await apiFetch({
      url: '/admin-update-property-permissions',
      body: {
        propertyIds: [propertyId],
        permissionsToCreate: Array.from(permissionsToCreate),
        permissionsToDelete: Array.from(permissionsToDelete),
      },
    }).catch(() => {
      showNotification({
        message: translate.phrases.validation('{{label}} could not be updated', {
          label: translate.phrases.banyanApp('Permissions'),
        }),
        type: 'error',
      })
    })

    setIsLoading(false)

    showNotification({
      message: translate.phrases.banyanApp('Permissions successfully updated'),
      type: 'success',
    })

    handleResetAndClose(true)
  }

  return (
    <ModalDrawer
      title={modalTitle}
      opened={opened}
      onClose={handleClickClose}
      zIndex={SharedSettings.DEFAULT_MODAL_DRAWER_Z_INDEX + 10}
    >
      <LoadingOverlay visible={loading} />
      <div>
        <Card>
          <h3 css={{ marginTop: 0 }}>{translate.phrases.banyanApp('Attach Permissions to Property')}</h3>

          {!loading && permissions && (
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                borderBottom: `1px solid ${colors.grey200}`,
              }}
            >
              <HierarchicalSelection
                key={property.id}
                searchString={searchString ?? ''}
                setSearchString={setSearchString}
                setShowOnlyChecked={setShowOnlyChecked}
                showOnlyChecked={showOnlyChecked}
                title={translate.phrases.banyanApp('Permissions')}
                width={'100%'}
                items={propertyPermissions}
                renderItem={({ index, style, data }) => (
                  <PermissionRow
                    permission={data[index]}
                    selected={selectedPermissions.includes(data[index].id)}
                    hasEditPermission={hasSetPropertyPermissionsPermission}
                    style={style}
                    setSelected={(selected, permissionId: TAdminPermission['id']) => {
                      if (selected) {
                        setPermissionsToCreate((prev) => new Set(prev).add(permissionId))

                        setPermissionsToDelete((prev) => {
                          const newSet = new Set(prev)

                          newSet.delete(permissionId)

                          return newSet
                        })
                      } else {
                        setPermissionsToCreate((prev) => {
                          const newSet = new Set(prev)

                          newSet.delete(permissionId)

                          return newSet
                        })

                        setPermissionsToDelete((prev) => new Set(prev).add(permissionId))
                      }

                      setSelectedPermissions((prev) => {
                        if (selected) {
                          return [...prev, permissionId]
                        } else {
                          return prev.filter((p) => p !== permissionId)
                        }
                      })
                    }}
                  />
                )}
              />

              {hasSetPropertyPermissionsPermission && (
                <CancelSaveButtonGroup
                  onCancel={handleClickClose}
                  onSave={handleSave}
                  saveDisabled={!hasChanges}
                />
              )}
            </div>
          )}
        </Card>

        <Card>
          <Group justify="space-between" align="center" css={{ marginBottom: '1rem' }}>
            <h3 css={{ margin: 0 }}>
              {translate.phrases.banyanApp('Effective Permissions Currently Attached to Property')}
            </h3>

            {hasChanges && (
              <Text c="dimmed" fz="sm">
                {translate.phrases.placeholder(
                  'Effective permissions will be updated after changes are saved',
                )}
              </Text>
            )}
          </Group>

          {effectivePermissions && (
            <div>
              <h4 css={{ margin: 0 }}>
                {translate.phrases.banyanApp('Property Permissions ({{count}})', {
                  count: effectivePermissions.length,
                })}
              </h4>

              <div css={{ display: 'flex', flexDirection: 'column' }}>
                <ul>
                  {effectivePermissions.sort().map(({ permission_id: permissionId, description }) => (
                    <li key={permissionId}>
                      <span css={{ fontWeight: 'bold' }}>{permissionId}</span>
                      {' - '}
                      {description}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {effectivePermissions.length === 0 && (
            <div css={{ padding: 20 }}>{translate.phrases.banyanApp('No Permissions')}</div>
          )}
        </Card>
      </div>

      <ConfirmationSettingsModal
        confirmModalOpened={showConfirmCancelWithoutSaving}
        setConfirmModalOpened={setShowConfirmCancelWithoutSaving}
        handleResetAndClose={() => {
          handleResetAndClose()
        }}
        handleUpdate={handleSave}
      />
    </ModalDrawer>
  )
}

const MemoizedPermissionsModal = memo(PermissionsModal, isEqual)

export { MemoizedPermissionsModal as PermissionsModal }
